import { useCallback, useEffect, useState } from "react"
import { FaChevronLeft } from "react-icons/fa"
import { useNavigate, useSearchParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import EventTemplatesSkeleton from "@fuse/sharedComponents/loader/TemplatesSkeletonLoader"
import { TemplatesData, TTemplateEvent } from "@letshang/constants"
import { ROUTES } from "src/App"

import { getEventTemplatesAPI } from "@api/firebaseWrappedApis"

import { staticTemplates } from "./staticTemplates"

const bgImage = "/assets/alpha-event-not-found.svg"

export const templateCategories = {
  Religious: ["Christian", "Muslim", "Jewish", "Hindu", "Buddhist"],
  Sporting: [
    "Soccer",
    "Basketball",
    "Paint ball",
    "Tennis",
    "Golf",
    "Pickleball",
    "Online games",
  ],
  Underground: [
    "DJ Sets",
    "Live Music events",
    "Secret parties",
    "Afterparties",
    "Others",
  ],
  Organizers: ["Graduation parties", "Baby shower", "Birthday", "Casual Hang"],
}

export const TemplateCard = ({
  template,
  onTemplateClick,
}: {
  template: TTemplateEvent
  onTemplateClick: (template: TTemplateEvent) => void
}) => (
  <div
    onClick={() => onTemplateClick(template)}
    className="min-w-[300px] max-w-[300px] aspect-square mx-2 first:ml-0 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col cursor-pointer"
  >
    <BackgroundWrapper bgImage={template.flyerImage} className="h-full">
      <div className="w-full h-full"></div>
      <div className="p-4 bg-gradient-to-t from-[#000000aa] to-transparent">
        <h3 className="text-xl text-white font-semibold mb-2">
          {template.title}
        </h3>
      </div>
    </BackgroundWrapper>
  </div>
)

const CategorySection = ({
  title,
  description,
  data,
  onTemplateClick,
}: {
  title: string
  description: string
  data: TTemplateEvent[]
  onTemplateClick: (template: TTemplateEvent) => void
}) => {
  if (data && data.length > 0) {
    return (
      <div className="mb-12">
        <h2 className="text-2xl text-white text-left font-semibold mb-2">
          {title} Events
        </h2>
        <p className="text-white/50 text-left mb-6">{description}</p>
        <div className="overflow-x-auto pb-4 -mx-4 px-4 no-scrollbar">
          <div className="flex space-x-4">
            {data.map((template) => (
              <TemplateCard
                key={template.eventId}
                template={template}
                onTemplateClick={onTemplateClick}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }
}

const EventTemplates = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<TemplatesData>(staticTemplates)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleStartFromScratch = useCallback(
    () => navigate(ROUTES.CREATE_EVENT, {}),
    [navigate],
  )

  // get current category from url
  const [params, _] = useSearchParams()
  const [currentCategory, setCurrentCategory] = useState("")
  const categoryFromUrl = params.get("category")

  const handleTemplateClick = useCallback(
    (template: TTemplateEvent) => {
      navigate(ROUTES.CREATE_EVENT, {
        state: {
          template: template,
          category: currentCategory,
        },
      })
    },
    [navigate, currentCategory],
  )

  useEffect(() => {
    if (categoryFromUrl) {
      setCurrentCategory(
        categoryFromUrl?.charAt(0)?.toUpperCase() +
          categoryFromUrl?.slice(1).toLowerCase(),
      )
    }
  }, [categoryFromUrl])

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response: TemplatesData = (await getEventTemplatesAPI(
          null,
        )) as TemplatesData

        // Merge API data with static data, excluding duplicates
        const mergedData: TemplatesData = {
          groupedTemplates: {},
        }

        // Process each category
        Object.keys(templateCategories).forEach((category: string) => {
          const staticTemplates = data.groupedTemplates[category] || []
          const apiTemplates = response.groupedTemplates[category] || []

          // Create a Set of existing template IDs
          const existingIds = new Set(staticTemplates.map((t) => t.id))

          // Filter out duplicates from API templates
          const uniqueApiTemplates = apiTemplates.filter(
            (t) => !existingIds.has(t.id),
          )

          // Combine static and unique API templates
          mergedData.groupedTemplates[category] = [
            ...staticTemplates,
            ...uniqueApiTemplates,
          ]
        })

        setData(mergedData)
        setLoading(false)
      } catch (err) {
        // Silently fail and keep showing static templates
        setLoading(false)
      }
    }

    fetchTemplates()
  }, [])

  const handleBackPress = () => {
    navigate(-1)
  }

  const Header: React.FC = () => {
    return (
      <div className="fixed top-0 left-0 right-0 z-20">
        <div className="absolute inset-0 bg-[#2525258C] backdrop-blur-md"></div>
        <div className="relative flex items-center px-4 md:px-12 py-4 md:py-4">
          <div
            onClick={handleBackPress}
            className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <span className="text-white">
              <FaChevronLeft color="white" size={20} />
            </span>
          </div>
          <h1 className="ml-4 text-2xl md:text-3xl text-white font-semibold">
            Event Templates
          </h1>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <BlurWrapper
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="h-full"
      >
        <BackgroundWrapper bgImage={bgImage} className="min-h-screen">
          <EventTemplatesSkeleton onTemplateClick={handleTemplateClick} />
        </BackgroundWrapper>
      </BlurWrapper>
    )
  }

  if (error) {
    return (
      <BackgroundWrapper bgImage={bgImage} className="min-h-screen">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-white text-xl">{error}</div>
        </div>
      </BackgroundWrapper>
    )
  }

  return (
    <BlurWrapper
      overlayOpacityDefault="75"
      overlayColorDefault="dark"
      className="h-full"
    >
      <BackgroundWrapper bgImage={bgImage} className="min-h-screen">
        <div className="min-h-screen">
          <Header />
          <div className="max-w-7xl mx-auto px-4 py-8 mt-20 md:mt-32">
            {templateCategories[currentCategory] &&
              templateCategories[currentCategory].map(
                (category: string, index: number) => {
                  const filteredTemplates =
                    data?.groupedTemplates[currentCategory]?.filter(
                      (template) =>
                        template.tags?.some(
                          (tag) => tag.toLowerCase() === category.toLowerCase(),
                        ),
                    ) || []

                  return (
                    <CategorySection
                      key={index}
                      title={category}
                      description={`Create sacred moments through ${category} traditions and celebrations.`}
                      data={filteredTemplates}
                      onTemplateClick={handleTemplateClick}
                    />
                  )
                },
              )}
          </div>
        </div>
        <div className="max-w-screen sticky bottom-0 px-4 md:px-12 flex justify-end ">
          <div className="p-6 w-full md:px-2 md:w-96 md:self-end  px-0">
            <button
              className="rounded-xl py-6 w-full text-white text-xl bg-white/10 backdrop-blur bg-gradient-to-br from-white/40 via-transparent via-50% to-white/10"
              onClick={handleStartFromScratch}
            >
              <span className="mr-3">✏️️</span>
              <span>Start from scratch</span>
            </button>
          </div>
        </div>
      </BackgroundWrapper>
    </BlurWrapper>
  )
}

export default EventTemplates
